import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../../context/ThemeContext';
import { adminAPI } from '../../services/api';
import { Package, Search, Eye, X, Check, Loader2, Truck } from 'lucide-react';

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  processing: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  shipped: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  delivered: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  cancelled: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
};

const AdminOrders = () => {
  const { isDarkMode } = useTheme();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [updating, setUpdating] = useState(false);

  const load = async () => {
    try {
      const res = await adminAPI.getOrders({ status: statusFilter || undefined, skip: 0, limit: 100 });
      setOrders(res.data);
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [statusFilter]);

  const updateStatus = async (orderId, status, tracking = '') => {
    setUpdating(true);
    try {
      await adminAPI.updateOrderStatus(orderId, { status, tracking_number: tracking });
      await load();
      if (selectedOrder?.id === orderId) {
        setSelectedOrder({ ...selectedOrder, status, tracking_number: tracking });
      }
    } catch {}
    finally { setUpdating(false); }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`text-3xl font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>Órdenes</h1>
          <p className={`mt-2 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Gestiona los pedidos de los clientes</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="p-2 border rounded-xl dark:bg-gray-700 dark:border-gray-600 text-sm">
            <option value="">Todas</option>
            <option value="pending">Pendientes</option>
            <option value="processing">Procesando</option>
            <option value="shipped">Enviadas</option>
            <option value="delivered">Entregadas</option>
            <option value="cancelled">Canceladas</option>
          </select>
        </div>
      </div>

      <div className={`${isDarkMode ? 'bg-gray-800/80' : 'bg-white/80'} backdrop-blur-sm rounded-3xl p-6 border shadow-sm`}>
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></div>
        ) : orders.length === 0 ? (
          <div className="text-center py-12 text-gray-500">No hay órdenes</div>
        ) : (
          <div className="space-y-3">
            {orders.map((order) => (
              <div key={order.id}
                className={`p-4 rounded-xl border ${isDarkMode ? 'border-gray-700 hover:bg-gray-700/30' : 'border-gray-100 hover:bg-gray-50'} cursor-pointer transition-colors`}
                onClick={() => setSelectedOrder(order)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <Package className="w-5 h-5 text-indigo-600" />
                    <div>
                      <p className="font-semibold text-sm">{order.order_number}</p>
                      <p className="text-xs text-gray-500">Usuario #{order.user_id} · {new Date(order.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusColors[order.status] || ''}`}>
                      {order.status}
                    </span>
                    <span className="font-bold">${order.total_amount?.toFixed(2)}</span>
                    <Eye className="w-4 h-4 text-gray-400" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {selectedOrder && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedOrder(null)}>
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl p-6 max-w-2xl w-full shadow-2xl max-h-[90vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold">Orden {selectedOrder.order_number}</h2>
              <button onClick={() => setSelectedOrder(null)}><X className="w-5 h-5" /></button>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <p className="text-sm text-gray-500">Estado</p>
                <span className={`px-3 py-1 rounded-full text-xs font-medium inline-block mt-1 ${statusColors[selectedOrder.status] || ''}`}>
                  {selectedOrder.status}
                </span>
              </div>
              <div>
                <p className="text-sm text-gray-500">Pago</p>
                <p className="font-medium capitalize">{selectedOrder.payment_status}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Método</p>
                <p className="font-medium">{selectedOrder.payment_method}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Total</p>
                <p className="font-bold text-lg">${selectedOrder.total_amount?.toFixed(2)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Subtotal</p>
                <p>${selectedOrder.subtotal?.toFixed(2)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">VU Total</p>
                <p>${selectedOrder.total_vu?.toFixed(2)}</p>
              </div>
            </div>

            {selectedOrder.items?.length > 0 && (
              <div className="mb-6">
                <h3 className="font-semibold mb-3">Productos</h3>
                <div className="space-y-2">
                  {selectedOrder.items.map((item) => (
                    <div key={item.id} className="flex justify-between text-sm py-2 border-b dark:border-gray-700">
                      <span>{item.product_name} x{item.quantity}</span>
                      <span>${(item.price * item.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="border-t dark:border-gray-700 pt-4">
              <h3 className="font-semibold mb-3">Actualizar Estado</h3>
              <div className="flex flex-wrap gap-2">
                {['pending', 'processing', 'shipped', 'delivered', 'cancelled'].map((s) => (
                  <button key={s}
                    onClick={() => updateStatus(selectedOrder.id, s)}
                    disabled={updating || selectedOrder.status === s}
                    className={`px-4 py-2 rounded-xl text-sm font-medium border transition-colors ${
                      selectedOrder.status === s
                        ? 'bg-indigo-600 text-white border-indigo-600'
                        : 'hover:bg-gray-100 dark:hover:bg-gray-700 dark:border-gray-600'
                    } disabled:opacity-50`}
                  >
                    {s.charAt(0).toUpperCase() + s.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {selectedOrder.is_physical && (
              <div className="border-t dark:border-gray-700 pt-4 mt-4">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Truck className="w-4 h-4" /> Seguimiento de Envío
                </h3>
                <div className="flex gap-2">
                  <input
                    placeholder="Número de seguimiento"
                    defaultValue={selectedOrder.tracking_number || ''}
                    id="tracking-input"
                    className="flex-1 p-3 border rounded-xl dark:bg-gray-700 dark:border-gray-600"
                  />
                  <button
                    onClick={() => {
                      const input = document.getElementById('tracking-input');
                      updateStatus(selectedOrder.id, selectedOrder.status, input?.value || '');
                    }}
                    disabled={updating}
                    className="px-4 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-50"
                  >
                    <Check className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default AdminOrders;
